import React, { useState, useCallback } from 'react';
import { formatMessageTime } from '../../utils/dateUtils';

/** Detecta el ícono de FA según extensión del archivo */
const getDocIcon = (name) => {
    if (!name) return 'fa-file-o';
    const ext = name.split('.').pop()?.toLowerCase();
    switch (ext) {
        case 'pdf': return 'fa-file-pdf-o';
        case 'doc': case 'docx': return 'fa-file-word-o';
        case 'xls': case 'xlsx': return 'fa-file-excel-o';
        case 'ppt': case 'pptx': return 'fa-file-powerpoint-o';
        case 'zip': case 'rar': case '7z': return 'fa-file-archive-o';
        default: return 'fa-file-o';
    }
};

const MessageBubble = ({ message }) => {
    const [lightbox, setLightbox] = useState(null);
    // URL local que puede actualizarse si el SAS expira
    const [urlMedia, setUrlMedia] = useState(message.urlMedia);
    const [renovando, setRenovando] = useState(false);
    const [renovacionFallida, setRenovacionFallida] = useState(false);
    const [zoom, setZoom] = useState(1);
    const [showSender, setShowSender] = useState(null);

    const isIncoming = message.direccion === 'entrante';
    const isSent = message.direccion === 'saliente';
    const tipo = (message.tipoMensaje || 'text').toLowerCase();
    const isSticker = tipo === 'sticker';

    /** Llama al backend para renovar el SAS del archivo expirado */
    const renovarUrl = useCallback(async () => {
        if (renovando || renovacionFallida || !message.id) return;
        setRenovando(true);
        try {
            const token = localStorage.getItem('token');
            const server = process.env.REACT_APP_API_ROUTE;
            const res = await fetch(
                `${server}/whatsappchat/mensajes/${message.id}/renovar-url`,
                {
                    method: 'POST',
                    headers: { 'Authorization': `Bearer ${token}` }
                }
            );
            if (res.ok) {
                const data = await res.json();
                if (data.urlMedia) {
                    setUrlMedia(data.urlMedia);
                } else {
                    setRenovacionFallida(true);
                }
            } else {
                setRenovacionFallida(true);
            }
        } catch {
            setRenovacionFallida(true);
        } finally {
            setRenovando(false);
        }
    }, [message.id, renovando, renovacionFallida]);

    const handleMediaError = useCallback(() => {
        renovarUrl();
    }, [renovarUrl]);

    /** Renderiza el contenido multimedia del mensaje según su tipo */
    const renderMedia = () => {
        const { tipoMensaje, contenido, nombreArchivo } = message;
        const t = (tipoMensaje || 'text').toLowerCase();

        if (renovando) {
            return (
                <div className="msg-media-placeholder">
                    <i className="fa fa-refresh fa-spin"></i> Renovando enlace...
                </div>
            );
        }

        switch (t) {
            case 'image':
                return urlMedia ? (
                    <div className="msg-media">
                        <img
                            src={urlMedia}
                            alt={nombreArchivo || 'Imagen'}
                            className="msg-image"
                            onClick={() => setLightbox(urlMedia)}
                            loading="lazy"
                            onError={handleMediaError}
                        />
                        {contenido && !contenido.startsWith('📷') && (
                            <p className="msg-caption">{contenido}</p>
                        )}
                    </div>
                ) : (
                    <div className="msg-media-placeholder">
                        <i className="fa fa-image"></i> Imagen no disponible
                    </div>
                );

            case 'audio':
                return urlMedia ? (
                    <div className="msg-media">
                        <audio controls className="msg-audio" onError={handleMediaError}>
                            <source src={urlMedia} type="audio/ogg" />
                            <source src={urlMedia} type="audio/mpeg" />
                            <source src={urlMedia} type="audio/mp4" />
                        </audio>
                    </div>
                ) : (
                    <div className="msg-media-placeholder">
                        <i className="fa fa-microphone"></i> Audio no disponible
                    </div>
                );

            case 'video':
                return urlMedia ? (
                    <div className="msg-media">
                        <video controls className="msg-video" preload="metadata" onError={handleMediaError}>
                            <source src={urlMedia} type="video/mp4" />
                        </video>
                        {contenido && !contenido.startsWith('🎬') && (
                            <p className="msg-caption">{contenido}</p>
                        )}
                    </div>
                ) : (
                    <div className="msg-media-placeholder">
                        <i className="fa fa-video-camera"></i> Video no disponible
                    </div>
                );

            case 'document':
                return urlMedia ? (
                    <a
                        href={urlMedia}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="msg-document"
                        onClick={async (e) => {
                            // Verificar si el SAS sigue válido antes de abrir
                            try {
                                const head = await fetch(urlMedia, { method: 'HEAD' });
                                if (!head.ok) {
                                    e.preventDefault();
                                    await renovarUrl();
                                }
                            } catch {
                                e.preventDefault();
                                await renovarUrl();
                            }
                        }}
                    >
                        <i className={`fa ${getDocIcon(nombreArchivo)}`}></i>
                        <span className="msg-doc-name">{nombreArchivo || contenido || 'Documento'}</span>
                        <i className="fa fa-download"></i>
                    </a>
                ) : (
                    <div className="msg-media-placeholder">
                        <i className="fa fa-file-o"></i> {nombreArchivo || 'Documento no disponible'}
                    </div>
                );

            case 'sticker':
                return urlMedia ? (
                    <img
                        src={urlMedia}
                        alt="Sticker"
                        className="msg-sticker"
                        loading="lazy"
                        onError={handleMediaError}
                    />
                ) : (
                    <span style={{ fontSize: '64px' }}>🏷️</span>
                );

            case 'location': {
                let coords = null;
                try { coords = JSON.parse(urlMedia); } catch (e) { /* ignore */ }
                return (
                    <div className="msg-location">
                        <div className="msg-location-header">
                            <i className="fa fa-map-marker" style={{ color: '#e53935', fontSize: 18 }}></i>
                            <span>{contenido || 'Ubicación compartida'}</span>
                        </div>
                        {coords && (
                            <a
                                href={`https://www.google.com/maps?q=${coords.latitude},${coords.longitude}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="msg-location-link"
                            >
                                <i className="fa fa-external-link"></i> Ver en Google Maps
                            </a>
                        )}
                    </div>
                );
            }

            case 'reaction':
                return (
                    <span style={{ fontSize: '32px', lineHeight: 1 }}>
                        {contenido?.replace('[reaction] ', '')}
                    </span>
                );

            case 'text':
            default:
                return <span>{contenido}</span>;
        }
    };

    return (
        <>
            <div
                className={`message-bubble ${isIncoming ? 'incoming' : 'outgoing'} ${isSticker ? 'sticker-bubble' : ''}`}
                // title={message.enviadoPor ? `Enviado por: ${message.enviadoPor}` : ''} 
                onClick={() => setShowSender(showSender === message.id ? null : message.id)}
            >
                {showSender === message.id && (
                <div className="message-sender-tooltip">
                    Enviado por: {message.enviadoPor}
                </div>
                )}

                <div className="message-content">
                    {renderMedia()}
                    <div className="message-meta">
                        <span className="message-time">
                            {formatMessageTime(message.fechaMensaje)}
                        </span>
                        {isSent && (
                            <span className="message-status" title={
                                message.estadoEnvio === 'read'      ? 'Leído' :
                                message.estadoEnvio === 'delivered' ? 'Entregado' :
                                message.estadoEnvio === 'sent'      ? 'Enviado' :
                                message.estadoEnvio === 'failed'    ? 'Error al enviar' :
                                'Enviando...'
                            }>
                                {message.estadoEnvio === 'read' ? (
                                    /* Ojo azul — leído */
                                    <i className="fa fa-eye" style={{ color: '#53bdeb', fontSize: 13 }}></i>
                                ) : message.estadoEnvio === 'delivered' ? (
                                    /* Doble check gris — entregado */
                                    <span style={{ color: '#999', fontSize: 13, letterSpacing: '-3px' }}>
                                        <i className="fa fa-check"></i>
                                        <i className="fa fa-check"></i>
                                    </span>
                                ) : message.estadoEnvio === 'failed' ? (
                                    /* X roja — falló */
                                    <i className="fa fa-times-circle" style={{ color: '#e53935', fontSize: 13 }}></i>
                                ) : (
                                    /* Check simple gris — enviado o pendiente */
                                    <i className="fa fa-check" style={{ color: '#999', fontSize: 13 }}></i>
                                )}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Lightbox para imágenes ampliadas */}
            {lightbox && (
                <div className="chat-lightbox"
                    onClick={() => {setLightbox(null); setZoom(1);}}
                    >
                    <button className="chat-lightbox-close" onClick={(e) => {
                        e.stopPropagation();
                        setLightbox(null);
                        setZoom(1);
                    }}>
                        <i className="fa fa-times"></i>
                    </button>

                     <div
                        className="zoom-controls"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <button
                            onClick={() =>
                                setZoom(prev => Math.min(prev + 0.25, 5))
                            }
                        >
                            <i className='fa fa-search-plus'/>
                        </button>

                        <button
                            onClick={() =>
                                setZoom(prev => Math.max(prev - 0.25, 1))
                            }
                        >
                             <i className='fa fa-search-minus'/>
                        </button>
                    </div>
                      <div
                        className="chat-lightbox-content"
                        onClick={(e) => e.stopPropagation()}
                        >
                    <img
                        src={lightbox}
                        alt="Imagen ampliada"
                        style={{
                            transform: `scale(${zoom})`,
                            transition: "transform 0.01s ease",
                            transformOrigin: "top center",
                            cursor: zoom > 1 ? "grab" : "default",
                            // maxWidth: "90%",
                            // maxHeight: "90%"
                        }} />
                </div>
            </div>
            )}
        </>
    );
};

export default MessageBubble;